import NotificationBar from 'components/notifications/NotificationBar';
import FloatingTooltip from 'components/watchlist/FloatingTooltip';
import WatchlistTracking from 'components/watchlist/WatchlistTracking';
import HttpService from 'services/HttpService';
import bfCachePersists from 'helpers/bfCachePersists';
import UrlHelper from 'helpers/UrlHelper';
import { getLocale } from 'services/LocalizationService.js';
import appendSpotlightTracking from '../../carousels/helpers/spotlightTracking';
import { dispatchCustomEvent } from '../highlight/helpers';
import Highlight from '../highlight/Highlight';

const CONTENT_SHOW = 'show';
const CONTENT_MOVIE = 'movie';

export default class WatchlistCta {
    static IN_WATCHLIST_CLASS = 'in_watchlist';
    static cycleCount = 0;

    constructor(el = null, isMarquee = false) {
        this.content = null;
        this.contentType = null;
        this.contentId = null;
        this._ = el;
        this._cta = null;
        this._tooltipTime = 4500;
        this._tooltipTimeout = null;
        this._http = new HttpService();
        this._notificationBar = null;
        this._floatingTooltip = null;
        this._tracking = null;
        this._apiErrorMsg = getLocale('sorry_we_cant_add_or_remove_items_right_now');
        this._apiErrorTrackingMessage = 'API Error';
        this._limitErrorTrackingMessage = 'Limit Error';
        this._isSafari = this.isSafari();
        this._referrerUrl = document.referrer;
        this._actionTrigger = false;
        this._aaLink = null;
        this.watchlistService = window._watchlistService;

        // Avoids cached page when going back/forth in browser
        bfCachePersists(() => {
            if (!isMarquee) {
                window.location.reload();
            }
        });

        this.docReady(this.init());
    }

    init() {
        const self = this;

        // Inits tracking
        this.initTracking();

        // Validate that there's watchlist component element
        if (!this.getComponentElement()) return false;

        self._ = this.getComponentElement();

        self._cta = self._.querySelector('.watchlistCta');
        self._aaLink = self._cta.getAttribute('aa-link') ?? '';
        self.content = $(self._).data();
        self.contentType = self.content.content_type;
        self.contentId = self.content.content_id;
        self.content.on_watchlist = this.isOnWatchlist();

        this._notificationBar = new NotificationBar();
        this._floatingTooltip = new FloatingTooltip(this.contentType === CONTENT_MOVIE);

        let cta = this._cta;
        cta.parentElement.addEventListener('click', (e) => {
            e.preventDefault();
            self.ctaClickHandler();
        });
        cta.parentElement.addEventListener('blur', () => console.log('blur event'));

        document.addEventListener(Highlight.STATE_CAROUSEL_EVENT_TYPE, async () => {
            await self.handleWatchlistCtaState();
        });

        // Checks if item is already on watchlist
        this.handleWatchlistCtaState();

        // Adding "initialized" class
        cta.parentElement.classList.add('initialized');
        // Adding class to identify content type page
        if (CBS.Registry.Features.movie_detail_page === false) {
            cta.parentElement.classList.add(
                self.contentType === CONTENT_SHOW ? 'in_show_page' : 'in_movie_page_international',
            );
        } else {
            cta.parentElement.classList.add(
                self.contentType === CONTENT_SHOW ? 'in_show_page' : 'in_movie_page',
            );
        }

        // Adding "content id" and "content type"
        cta.setAttribute('data-content_type', self.contentType);
        cta.setAttribute('data-content_id', self.contentId);
    }

    getComponentElement() {
        if (this._) {
            return $(this._).find('.watchlist_wrapper')[0];
        } else {
            return document.querySelector('div.watchlist_wrapper');
        }
    }

    async handleWatchlistCtaState() {
        let cta = this._cta;

        if (await this.isOnWatchlist()) {
            cta.classList.add('in_watchlist');
        } else {
            cta.classList.remove('in_watchlist');
        }
    }

    async isOnWatchlist() {
        while (!this.watchlistService.loaded) {
            await new Promise((resolve) => setTimeout(resolve, 50));
        }

        return this.watchlistService.isContentInWatchlist(this.contentId);
    }

    ctaClickHandler() {
        if (this._actionTrigger) return false;

        let cta = this._cta;
        this._actionTrigger = true;

        // adds to watchlist or removes from watchlist
        cta.classList.contains('in_watchlist') ? this.removeFromWatchlist() : this.addToWatchlist();
    }

    showLimitReachedTooltip(message = '') {
        let cta = this._cta;

        if (this.isMobileLandscapeMax()) {
            this._floatingTooltip.slideIn(message);
        } else {
            let tooltip = cta.parentElement.querySelector('span.tooltip');
            tooltip.innerHTML = `<div>${message}</div>`;
            tooltip.classList.add('visible');
            this.createTooltipTimeout(tooltip);
        }

        // tracks limit reached API error
        if (this._tracking) {
            this._tracking.trackApiError({
                ctaText: this._limitErrorTrackingMessage,
                ...this.getTrackingParams(),
            });
        }
    }

    showErrorMessage() {
        this._notificationBar.showMessage(this._apiErrorMsg, false);

        // tracks API error
        if (this._tracking) {
            this._tracking.trackApiError({
                ctaText: this._apiErrorTrackingMessage,
                ...this.getTrackingParams(),
            });
        }
    }

    refreshMYListCarousel() {
        let myListCarousel = document.getElementById('my-list');
        if (myListCarousel) {
            dispatchCustomEvent(this._, Highlight.MY_LIST_CAROUSEL_EVENT_TYPE);
        }
    }

    addToWatchlist() {
        const self = this;
        let cta = this._cta;

        // Adds to watchlist
        return self
            .watchlistAddXhrCall()
            .then((res) => {
                if (res.success) {
                    cta.classList.add('in_watchlist');

                    self.refreshMYListCarousel();

                    // tracks add action
                    if (this._tracking) this._tracking.trackAddAction(this.getTrackingParams());
                    // Adds pushstate (history) on watchlist modification
                    if (this.isReferrerHome()) {
                        if (this._isSafari) {
                            window.history.pushState({}, '', document.referrer);
                            return true;
                        }
                        window.history.replaceState({}, document.title, window.location.href);
                    }
                } else {
                    if (res.data.limit_reached) {
                        self.showLimitReachedTooltip(res.message);
                    } else {
                        this.showErrorMessage();
                    }
                }
            })
            .catch((err) => {
                this.showErrorMessage();
                console.log(`Watchlist CTA - ${err.message}`);
            })
            .finally(() => (this._actionTrigger = false));
    }

    watchlistAddXhrCall() {
        const xhrUrl = `${CBS.Registry.region.prefix}/user-profile/xhr/watchlist/${this.contentType}/${this.contentId}/add/`;

        return this.watchlistService.addContentToWatchlist(this.contentId, xhrUrl);
    }

    removeFromWatchlist() {
        const self = this;
        let cta = this._cta;

        return self
            .watchlistRemoveXhrCall()
            .then((res) => {
                if (res.success) {
                    cta.classList.remove('in_watchlist');

                    self.refreshMYListCarousel();

                    // tracks removal action
                    if (this._tracking) this._tracking.trackRemoveAction(this.getTrackingParams());
                    // Adds pushstate (history) on watchlist modification
                    if (this.isReferrerHome()) {
                        if (this._isSafari) {
                            window.history.pushState({}, '', document.referrer);
                            return true;
                        }
                        window.history.replaceState({}, document.title, window.location.href);
                    }
                } else {
                    this.showErrorMessage();
                }
            })
            .catch((err) => {
                this.showErrorMessage();
                console.log(`Watchlist CTA - ${err.message}`);
            })
            .finally(() => (this._actionTrigger = false));
    }

    watchlistRemoveXhrCall() {
        const xhrUrl = `${CBS.Registry.region.prefix}/user-profile/xhr/watchlist/${this.contentType}/${this.contentId}/remove/`;
        return this.watchlistService.removeContentFromWatchlist(this.contentId, xhrUrl);
    }

    createTooltipTimeout(tooltip) {
        // If visible already
        if (tooltip.classList.contains('visible')) {
            clearTimeout(this._tooltipTimeout);
        }
        // establish timeout for hiding out the tooltip
        this._tooltipTimeout = setTimeout(
            () => tooltip.classList.remove('visible'),
            this._tooltipTime,
        );
    }

    isMobileLandscapeMax() {
        let windowWidth = this._isSafari ? $(window).width() : $(window).outerWidth();
        return windowWidth <= 550;
    }

    isMovie() {
        return this.contentType === CONTENT_MOVIE;
    }

    getTrackingParams() {
        if (this._aaLink !== undefined) {
            let aaLinkData = this._aaLink.split('|');

            let trackingObj = {
                pageType: CBS.Registry.homepage ? 'front_door' : this.isMovie() ? 'movie' : 'show',
                showSeriesId: !this.isMovie() ? this.contentId : '',
                showSeriesTitle: !this.isMovie()
                    ? (this.content.seriesTitle ?? aaLinkData[14])
                    : '',
                showGenre: !this.isMovie() ? this.content.genre : '',
                showEpisodeId: !this.isMovie() ? aaLinkData[16] : '',
                showEpisodeTitle: !this.isMovie() ? aaLinkData[5] : '',
                showEpisodeNumber: !this.isMovie() ? aaLinkData[7] : '',
                showSeasonNumber: !this.isMovie() ? aaLinkData[6] : '',
                movieId: this.isMovie() ? this.contentId : '',
                movieTitle: this.isMovie() ? this.content.title : '',
                movieGenre: this.isMovie() ? this.content.genre : '',
                contentLocked: aaLinkData[23] ? aaLinkData[23] : '',
            };

            // extra params for spotlight carousel
            const $spotlightCarousel = $(this._cta).closest('.spotlight-carousel');
            if ($spotlightCarousel) {
                trackingObj = appendSpotlightTracking($spotlightCarousel, trackingObj);
            }

            return trackingObj;
        }
    }

    initTracking(callback = function () {}) {
        CBS.Promises.Tracking.onLoad(() => {
            this._tracking = new WatchlistTracking(this._cta);
            callback();
        });
    }

    isReferrerHome() {
        const homeUrl = UrlHelper.getHomePageURL().replaceAll('/', '');
        let referrerUrl = document.createElement('a');
        referrerUrl.href = this._referrerUrl;
        referrerUrl = referrerUrl.pathname.replaceAll('/', '');
        return referrerUrl === homeUrl;
    }

    isSafari() {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    docReady(fn) {
        // see if DOM is already available
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            // call on next available tick
            setTimeout(fn, 1);
        } else {
            document.addEventListener('DOMContentLoaded', fn);
        }
    }

    setSlideCycle() {
        WatchlistCta.cycleCount++;
    }

    resetSlideCycle() {
        WatchlistCta.cycleCount = 0;
    }
}
